import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, type AvatarShape } from "../../components/Avatar.js";
import { Callout } from "../../components/Callout.js";
import { Icon } from "../../components/Icon.js";
import { fmt, dollarsToCents } from "../../lib/money.js";
import { postKidTransfer } from "../../api/kidTransfers.js";
import { useUser } from "../../auth/UserContext.js";
import type { KidBundle } from "../../api/me.js";

function isValidDollarAmount(s: string): boolean {
  if (!s) return false;
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return false;
  return Number(s) > 0;
}

export default function TransferRequest() {
  const { bundle, refresh } = useUser();
  const navigate = useNavigate();

  const [toKidId, setToKidId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!bundle || bundle.user.role !== "kid") return null;
  const kid = bundle as KidBundle;
  const siblings = kid.siblings ?? [];

  const amountValid = isValidDollarAmount(amount);
  const amtCents = amountValid ? dollarsToCents(amount) : 0;
  const overdraft = amountValid && amtCents > kid.account.balanceCents;
  const canSubmit = amountValid && !overdraft && toKidId.trim() !== "" && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await postKidTransfer({
        toKidId,
        amountCents: amtCents,
        note: note.trim() || `From ${kid.user.name}`,
      });
      await refresh();
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div
        className="card"
        style={{ maxWidth: 480, margin: "40px auto", textAlign: "center", padding: 40 }}
      >
        <Icon name="check" size={48} color="var(--pos)" />
        <h2 style={{ marginTop: 16 }}>Request sent!</h2>
        <p className="soft" style={{ marginTop: 8 }}>
          Your transfer is waiting for a parent to approve it.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
          <button className="btn btn-ghost" onClick={() => setSuccess(false)}>
            Send another
          </button>
          <button className="btn btn-brand" onClick={() => navigate("/kid/home")}>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  const selectedSibling = siblings.find((s) => s.rowKey === toKidId);

  return (
    <div style={{ maxWidth: 480, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 6 }}>Send money</h1>
      <p className="soft" style={{ marginBottom: 24 }}>
        Request a transfer to a sibling. A parent will approve it before the money moves.
      </p>

      <div className="card stack" style={{ gap: 20, padding: 28 }}>
        {/* Recipient picker */}
        <div className="input-group">
          <label className="label">Send to</label>
          {siblings.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                gap: 8,
              }}
            >
              {siblings.map((s) => {
                const color = s.color ?? "var(--brand)";
                const active = toKidId === s.rowKey;
                return (
                  <button
                    key={s.rowKey}
                    type="button"
                    onClick={() => setToKidId(s.rowKey)}
                    style={{
                      padding: "12px 8px",
                      borderRadius: 12,
                      background: active ? color : "var(--canvas-warm)",
                      color: active ? "white" : "var(--ink)",
                      border: "none",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    <Avatar
                      kid={{
                        name: s.name,
                        color,
                        avatarShape: (s.avatarShape as AvatarShape) ?? "circle",
                      }}
                      size={32}
                    />
                    {s.name}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="soft" style={{ fontSize: 13 }}>
              No siblings available to send to.
            </div>
          )}
        </div>

        {/* Amount */}
        <div className="input-group">
          <label className="label">Amount</label>
          <div className="row">
            <div style={{ fontSize: 28, fontFamily: "Fraunces, serif" }}>$</div>
            <input
              className="input"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0"
              autoFocus
            />
          </div>
          {overdraft && (
            <div style={{ fontSize: 12, color: "var(--neg)" }}>
              That&apos;s more than you have ({fmt(kid.account.balanceCents)}).
            </div>
          )}
        </div>

        {/* Note */}
        <div className="input-group">
          <label className="label">Note (optional)</label>
          <input
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Pokemon cards, lunch money"
            maxLength={120}
          />
        </div>

        {/* Preview */}
        {amountValid && !overdraft && selectedSibling && (
          <Callout iconName="transfer">
            <div style={{ fontSize: 13 }}>
              Sending {fmt(amtCents)} to <b>{selectedSibling.name}</b> — awaiting parent approval.
            </div>
          </Callout>
        )}

        {error && (
          <div style={{ fontSize: 12, color: "var(--neg)" }} role="alert">
            {error}
          </div>
        )}

        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => navigate("/kid/home")} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-brand" onClick={handleSubmit} disabled={!canSubmit}>
            {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>

      {/* Pending transfers section */}
      {(kid.pendingTransfers?.length ?? 0) > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 14 }}>Pending requests</h3>
          {kid.pendingTransfers.map((p) => {
            const isOutgoing = p.fromKidId === kid.user.rowKey;
            const siblingName =
              siblings.find((s) => s.rowKey === (isOutgoing ? p.toKidId : p.fromKidId))?.name ??
              (isOutgoing ? "another kid" : "someone");
            return (
              <div key={p.rowKey} className="txn-row">
                <div
                  className="txn-icon-wrap"
                  style={{
                    color: isOutgoing ? "var(--neg)" : "var(--pos)",
                    background: isOutgoing ? "oklch(0.95 0.03 27)" : "oklch(0.95 0.03 155)",
                  }}
                >
                  <Icon name="transfer" size={18} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{p.note}</div>
                  <div className="soft" style={{ fontSize: 12 }}>
                    {isOutgoing ? `To ${siblingName}` : `From ${siblingName}`} ·{" "}
                    <span style={{ color: "var(--brand)" }}>Pending approval</span>
                  </div>
                </div>
                <div className={`money money-sm ${isOutgoing ? "money-neg" : "money-pos"}`}>
                  {fmt(isOutgoing ? -p.amountCents : p.amountCents, { showSign: true })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
